/**
 * The goal netting bulging around the ball.
 *
 * Port of `Match::UpdateGoalNetting` (match.cpp:2159-2205). The original pulls the netting
 * mesh's own vertices toward the ball while `Ball::touchesNet` is true — hardest at the
 * vertex nearest the ball, falling off with distance, and tapered to nothing for the first
 * half metre past the goal line so the net stays flat where it meets the frame. `src/sim/
 * ball.ts` already computes and publishes `touchesNet` and the ball's own position; this
 * module is the rendering side the CLAUDE.md entry on the netting describes as still
 * missing ("Nothing draws that here yet, so a hard shot briefly passes through a rigid net
 * before settling against it").
 *
 * FINDING THE MESH: no position-based heuristic was needed. `goals.ase` converts to a glTF
 * mesh named "goals" with two primitives — one per material — and `toDrawItems` (geometry.ts)
 * already hands each primitive back as its own DrawItem with its own BufferGeometry. The
 * second material's recorded texture path is `media/textures/stadium/goalnetting.png`
 * (NETTING_TEXTURE below), which is a strictly more precise split than the original's own
 * `vertices[i].x < -pitchHalfW - 0.06` heuristic (`PrepareGoalNetting`, match.cpp:2140-2157):
 * that heuristic exists only to separate netting from woodwork inside one merged buffer,
 * and the converter already keeps them apart as separate primitives, confirmed against the
 * shipped `public/assets/meshes/stadiums/goals.glb` (primitive 1: 2448 vertices, bounding
 * box Y ±3.7 / Z 0..2.5 — exactly `GOAL.halfWidth`/`GOAL.height` in `src/sim/ball.ts`).
 *
 * SPACE: this mesh, like the ball mesh, is parented under `world` in main.ts, whose own
 * rotation performs the sim -> render conversion for its whole subtree — see the comment
 * beside `ballMesh.position.copy(...)` there ("Under `world`, so simulation metres go
 * straight in — no per-object conversion"). Its vertex data is loaded unconverted: the
 * glTF's own `extras.coordinateSystem` says so, and the netting primitive's bounding box
 * confirms it (Z is height, 0..2.5 m, not Y — this is still the simulation's Z-up frame).
 * So `update` below takes `ball.position` straight, with no `simToRender` call: converting
 * only the ball's side of the comparison would pull every vertex toward a point rotated 90
 * degrees away from where the ball actually is, against unconverted mesh data.
 *
 * THE LIVE-BUFFER TRAP (CLAUDE.md; the identical bug is pinned for the player mesh in
 * tests/players.test.ts's "skinning buffers" suite): `Float32BufferAttribute` always copies
 * whatever array it is constructed from (`super(new Float32Array(array), ...)`, verified
 * against three's own source). The constructor below takes the array back off the attribute
 * after attaching it, exactly like `cloneSkinnableGeometry` in render/humanoid.ts, and
 * `update` writes every frame into that returned reference, never into a plain array of its
 * own that the geometry has already stopped reading from.
 *
 * WHY POSITIONS ARE CLONED RATHER THAN DEFORMED IN PLACE: `AssetStore.mesh()` caches the
 * loaded glTF (`src/assets/loader.ts`), so the SAME BufferGeometry — the SAME position
 * attribute — comes back if a second match starts without a page reload; main.ts's own
 * comment on `leaveMatch` says so explicitly ("the next match reuses every decoded mesh").
 * Deforming that shared attribute in place would leave a second match's net starting from
 * however the first one's happened to end. A fresh, private, DynamicDrawUsage attribute is
 * attached instead, and the original interleaved position data is only ever read (through
 * getX/getY/getZ, which know its stride — see the interleaving trap documented above
 * `readVertexArrays` in humanoid.ts), never written.
 *
 * ONE DELIBERATE DIVERGENCE: the original snaps back to the rest shape the instant the ball
 * stops touching, in a single tick (`resetNetting`, match.cpp:2197-2206). On a screen rather
 * than in a printout that reads as a pop, so `update` eases back over EASE_MS instead. Not a
 * fidelity gap — a rendering call the C++ never had to make because it never draws anything
 * itself.
 *
 * ONE THING NOT CARRIED OVER: `UpdateGoalNetting` declares `float falloffDistance = 4.0f;`
 * and never reads it — the line that would have used it for a linear falloff is commented
 * out beside the ratio-based formula that actually shipped (match.cpp:2179-2181). Not
 * ported, because it does nothing in the original either.
 */

import { DynamicDrawUsage, Float32BufferAttribute, type BufferGeometry, type Vector3 } from 'three';
import { PITCH } from '../core/constants';
import { clamp } from '../core/math';

/** The converter's recorded path for goals.ase's second material (match.cpp:2140-2157's
 *  netting split, done here by material instead of by vertex position). */
export const NETTING_TEXTURE = 'media/textures/stadium/goalnetting.png';

/**
 * How long the net takes to settle back to its rest shape once the ball leaves it. Chosen
 * for feel — a small, quick relaxation rather than `resetGlide.ts`'s 400 ms, which bridges a
 * player teleport tens of metres wide — not measured, because nothing about "does a net look
 * like it's settling" is a match statistic. `docs/original-engine` and PENDING.md have
 * nothing to check it against: this is cosmetic and the original never draws it moving at
 * all.
 */
const EASE_MS = 300;

/** Smoothstep — zero velocity at both ends, matching resetGlide.ts's own easedProgress. */
function easedProgress(t: number): number {
  const c = clamp(t, 0, 1);
  return c * c * (3 - 2 * c);
}

export class GoalNetting {
  readonly geometry: BufferGeometry;
  private readonly attribute: Float32BufferAttribute;
  /** The undeformed shape, sim-space, never written after construction. */
  private readonly rest: Float32Array;
  /** The attribute's own backing array — taken back off it, per the trap above. */
  private readonly live: Float32Array;
  /** Vertex indices split by the sign of their REST x: [negative-x goal, positive-x goal],
   *  mirroring `nettingMeshes[0]`/`nettingMeshes[1]` in PrepareGoalNetting. */
  private readonly sideIndices: readonly [Int32Array, Int32Array];

  /** True for exactly one frame: the one right after touchesNet was last true. */
  private wasTouching = false;
  /** True while easing back to rest. */
  private returning = false;
  private readonly easeFrom: Float32Array;
  private easeStartMs = 0;

  constructor(geometry: BufferGeometry) {
    this.geometry = geometry;
    const source = geometry.getAttribute('position');
    if (!source) throw new Error('GoalNetting: geometry has no position attribute');
    const count = source.count;

    this.rest = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      this.rest[i * 3] = source.getX(i);
      this.rest[i * 3 + 1] = source.getY(i);
      this.rest[i * 3 + 2] = source.getZ(i);
    }
    this.easeFrom = new Float32Array(count * 3);

    // Own array, dynamic usage (rewritten every touching frame), and the array is taken
    // back off the attribute rather than kept from what was passed in — see the module
    // comment on the live-buffer trap.
    const attribute = new Float32BufferAttribute(this.rest.slice(), 3);
    attribute.setUsage(DynamicDrawUsage);
    geometry.setAttribute('position', attribute);
    this.attribute = attribute;
    this.live = attribute.array as Float32Array;

    const side0: number[] = [];
    const side1: number[] = [];
    for (let i = 0; i < count; i++) {
      (this.rest[i * 3]! < 0 ? side0 : side1).push(i);
    }
    this.sideIndices = [Int32Array.from(side0), Int32Array.from(side1)];
  }

  /**
   * Called once a render frame with `Ball`'s own fields, read straight — see the module
   * comment on why no coordinate conversion belongs here.
   */
  update(ballPosition: Vector3, touchesNet: boolean, nowMs: number): void {
    if (touchesNet) {
      this.pullTowardBall(ballPosition);
      this.wasTouching = true;
      this.returning = false;
      this.attribute.needsUpdate = true;
      return;
    }

    if (this.wasTouching) {
      // The instant the ball lets go: start easing from wherever the mesh actually is, not
      // from rest — there is no reason the net should be mid-bulge and mid-ease at once.
      this.easeFrom.set(this.live);
      this.easeStartMs = nowMs;
      this.returning = true;
      this.wasTouching = false;
    }

    if (!this.returning) return; // already settled; nothing to do until it touches again

    this.relax(nowMs);
    this.attribute.needsUpdate = true;
  }

  /**
   * Force the net back to rest immediately, with no easing.
   *
   * Call this before a match tears down. `AssetStore.mesh()` caches the loaded glTF
   * (see the module comment), so this same BufferGeometry comes back if another match
   * starts in the same session, and its constructor reads whatever shape is CURRENTLY on
   * this attribute as the new rest pose. `touchesNet` can stay true for several seconds
   * after a goal — PENDING.md/ball.ts: "It is live for the seconds between a goal and the
   * kick-off" — which is a real window for a player to quit mid-celebration. Without this,
   * the next match would inherit that bulge as its rest shape instead of the true one.
   */
  resetToRest(): void {
    this.live.set(this.rest);
    this.wasTouching = false;
    this.returning = false;
    this.attribute.needsUpdate = true;
  }

  /** match.cpp:2163-2193, one goal's worth: `sideID` picks which side the ball is on, since
   *  only one goal can ever be pressing against its net at a time. */
  private pullTowardBall(ball: Vector3): void {
    const side = this.sideIndices[ball.x < 0 ? 0 : 1]!;
    const rest = this.rest;
    const bx = ball.x;
    const by = ball.y;
    const bz = ball.z;

    let shortest = Infinity;
    for (let n = 0; n < side.length; n++) {
      const i = side[n]!;
      const dx = rest[i * 3]! - bx;
      const dy = rest[i * 3 + 1]! - by;
      const dz = rest[i * 3 + 2]! - bz;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (d < shortest) shortest = d;
    }

    // ball.cpp:361 / match.cpp:2183, and the identical shape in collideNetting's own
    // "slack": zero right at the goal line, full a half metre past it, off the ball's
    // POSITION (the original's own bug — reading this off momentum instead — is what made
    // the net permanently rigid; see the "netting" section of src/sim/ball.ts).
    const woodworkTensionBiasInv = clamp((Math.abs(bx) - PITCH.halfLength) * 2, 0, 1);

    for (let n = 0; n < side.length; n++) {
      const i = side[n]!;
      const rx = rest[i * 3]!;
      const ry = rest[i * 3 + 1]!;
      const rz = rest[i * 3 + 2]!;
      const dx = rx - bx;
      const dy = ry - by;
      const dz = rz - bz;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);

      let influence = Math.pow(clamp((shortest + 0.0001) / (d + 0.0001), 0, 1), 1.5);
      influence *= woodworkTensionBiasInv;
      // match.cpp:2185-2186 — an ease-in/ease-out remap of the ratio above, not a distance
      // falloff (see the module comment: `falloffDistance` is declared and never read).
      influence = Math.sin(influence * Math.PI - 0.5 * Math.PI) * 0.5 + 0.5;

      if (influence > 0) {
        this.live[i * 3] = rx * (1 - influence) + bx * influence;
        this.live[i * 3 + 1] = ry * (1 - influence) + by * influence;
        this.live[i * 3 + 2] = rz * (1 - influence) + bz * influence;
      }
    }
  }

  /** Ease every vertex on both sides back toward rest — matching the original resetting
   *  both goalIDs regardless of which one was actually deformed (match.cpp:2198-2204), which
   *  costs nothing extra since the untouched side is already sitting at rest. */
  private relax(nowMs: number): void {
    const t = (nowMs - this.easeStartMs) / EASE_MS;
    if (t >= 1) {
      this.live.set(this.rest);
      this.returning = false;
      return;
    }
    const factor = easedProgress(t);
    const from = this.easeFrom;
    const rest = this.rest;
    for (let k = 0; k < this.live.length; k++) {
      this.live[k] = from[k]! + (rest[k]! - from[k]!) * factor;
    }
  }
}
